import dateformat from 'dateformat';
import { compose, flatten, groupBy, mapValues } from 'lodash/fp';
import { DataCondition, DataType } from 'types';

import { parseApiError as commonParseApiError } from '../api/request';

export const parseApiError = (err) => {
    const error = commonParseApiError(err);
    return error.fields ? error.fields : { server: error.message };
};

export const serializeResource = (resource: any) => {
    return {
        ...resource,
        conditions: serializeConditions(resource.conditions),
        refinement_filters: resource.refinement_filters,
    };
};

export const deserializeResource = (resource: any) => {
    return {
        ...resource,
        conditions: deserializeConditions(resource.conditions),
        refinement_filters: resource.refinement_filters,
    };
};

export const serializeConditions = (
    selectedConditions: DataCondition[]
): {
    data_type: string;
    primary_entity_id: number[];
}[] => {
    if (selectedConditions == null) {
        return selectedConditions;
    }

    const conditionsIdsByType: Record<string, number[]> = compose(
        mapValues((conditionsByType: DataCondition[]) =>
            conditionsByType.map((i) => i.primary_entity_id)
        ),
        groupBy('data_type')
    )(selectedConditions);

    return Object.entries(conditionsIdsByType).map(([type, conditionsIds]) => ({
        data_type: type,
        primary_entity_id: conditionsIds,
    }));
};

export const deserializeConditions = (
    apiConditions: {
        data_type: DataType;
        primary_entity_id: number[];
    }[]
): DataCondition[] => {
    if (apiConditions == null) {
        return apiConditions;
    }

    return flatten(
        apiConditions.map(({ data_type, primary_entity_id }) =>
            primary_entity_id.map((id) => ({ data_type, primary_entity_id: id }))
        )
    );
};

export const getFileDownloadUrl = (resourceId: number): string =>
    `/file-resources/${resourceId}/download`;

export const getOrganizationFileDownloadUrl = (
    organizationId: number,
    resourceId: number
): string => `/organizations/${organizationId}/file-resources/${resourceId}/download`;

// Client portal: the organization is resolved from the session token.
export const getMyOrganizationFileDownloadUrl = (resourceId: number): string =>
    `/my-organization/file-resources/${resourceId}/download`;

export const serializeDateShort = (date: Date | null) => {
    if (date == null) {
        return date;
    }

    return dateformat(date, 'yyyy-mm-dd');
};

export const serializeToUtcDate = (date?: Date) => {
    if (date == null) {
        return date;
    }

    const isoDate = date.toISOString();
    return `${isoDate.substr(0, 10)} ${isoDate.substr(11, 8)}`;
};

// best-practice

export const serializeRichText =
    <T>(propertyName: keyof T) =>
    <R extends Partial<T>>(object: R): R => {
        if (object == null || object[propertyName] == null) {
            return object;
        }

        let result;
        try {
            result = JSON.stringify(object[propertyName]);
        } catch (error) {}

        return { ...object, [propertyName]: result };
    };

export const deserializeRichText =
    <T>(propertyName: keyof T) =>
    <R extends Partial<T>>(object: R): R => {
        if (object == null || object[propertyName] == null) {
            return object;
        }

        let result;
        try {
            result = JSON.parse(object[propertyName] as any);
        } catch (error) {}

        return { ...object, [propertyName]: result };
    };
